import type { OperationFailed, OperationResult } from "@skbkontur/operation-result";
import { createFailure, isFailure, isSuccess, tryExecute } from "@skbkontur/operation-result";
import type { WidgetDependenciesConstraint, WidgetFaultConstraint, WidgetGenericFault } from "../public-types.js";
import type { UnexpectedFault } from "../tryExecute/tryExecute.js";
import type { FetchMessage } from "./getFetchMessage.js";
import { getFetchMessage } from "./getFetchMessage.js";
import { InvalidArguments, ModuleLoadFailed, NoExport } from "./importWidgetModuleFaultType.js";
import { importWidgetModuleFromNamespace } from "./importWidgetModuleFromNamespace.js";

export * as ImportWidgetModuleFaultType from "./importWidgetModuleFaultType.js";

type InvalidArgumentsFault = WidgetGenericFault<typeof InvalidArguments>;

const createInvalidArgumentsFailure = (message: string): OperationFailed<InvalidArgumentsFault> =>
  createFailure({
    type: InvalidArguments,
    message,
  });

export type AddImportWidgetModuleFaults<TImportFault extends WidgetFaultConstraint> =
  | TImportFault
  | UnexpectedFault
  | InvalidArgumentsFault
  | WidgetGenericFault<typeof NoExport>
  | {
      readonly type: typeof ModuleLoadFailed;
      readonly message: string;
      readonly error: unknown;
      readonly fetchMessage: FetchMessage;
    };

type Params<TDependencies extends WidgetDependenciesConstraint> = {
  readonly widgetUrl: URL;
  readonly dependencies: TDependencies;
};

export async function importWidgetModule<
  TDependencies extends WidgetDependenciesConstraint,
  TFault extends WidgetFaultConstraint,
  TApi,
>({
  widgetUrl,
  dependencies,
}: Params<TDependencies>): Promise<OperationResult<AddImportWidgetModuleFaults<TFault>, TApi>> {
  if (!widgetUrl) {
    return createInvalidArgumentsFailure(`Widget url '${widgetUrl}' is invalid`);
  }

  if (!dependencies) {
    return createInvalidArgumentsFailure(`Dependencies '${dependencies}' is not an object`);
  }

  const importNamespaceResult = await tryExecute(
    () => import(/* webpackIgnore: true */ /* @vite-ignore */ widgetUrl.href)
  );
  if (isFailure(importNamespaceResult)) {
    return createFailure({
      type: ModuleLoadFailed,
      message: `Failed to load module '${widgetUrl.href}'`,
      error: importNamespaceResult.fault,
      fetchMessage: await getFetchMessage(widgetUrl),
    });
  }

  const importModuleResult = await importWidgetModuleFromNamespace<TDependencies, TFault, TApi>(
    importNamespaceResult.value,
    widgetUrl,
    dependencies
  );
  if (isSuccess(importModuleResult)) {
    return importModuleResult;
  }

  const { fault } = importModuleResult;
  switch (fault.type) {
    case "namespace-no-export":
      return createFailure({ type: NoExport, message: fault.message });
    case "passthrough":
      return createFailure(fault.inner);
  }
}
